import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ArrowLeft, Plus, Send, Trash2, Search } from "lucide-react";
import { fetchWithAuth } from "@/lib/auth-client";
import type {
  PurchaseOrderListItemDto,
  PurchaseOrderDetailDto,
  POLineItemDto,
  GRNCondition,
  CreateGRNFromPOsRequest,
  UpdateGRNRequest,
  GRNDetailDto,
} from "@procureflow/shared-types";
import {
  poLineRemaining,
  validateGRNBuildRows,
  type GRNBuildRow as GRNBuildRowModel,
} from "@/lib/grn-build";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// US-065 (m2m-document-linkage) — Create-GR-from-multiple-POs builder.
//
// A standalone form reached from a "Create GR" button on the GRN list. Each row
// has TWO cascading lookups: (1) pick a receivable source PO (Sent /
// PartiallyReceived with remaining receivable lines), (2) pick a line of that PO
// (only lines with remaining > 0). Each row also carries a received quantity
// (≤ remaining), a condition (Good / Damaged / Rejected), and optional notes.
// The GRN header carries a receiver name, date received, and optional delivery
// reference. The form prevents over-receiving (qty ≤ remaining), then POSTs
// /api/grns/from-pos and navigates to the new GRN detail.
//
// The per-PO receive path (grn/receive/$poId) remains the single-source case.

// US-077 — the same builder doubles as the draft-edit form when reached with
// ?editId=<grnId> (from the Edit affordance on a Recorded GRN). In edit mode it
// pre-loads the GRN's current lines/header and PUTs the changes (the source PO re-sync
// happens server-side); otherwise it POSTs a new GRN.
export const Route = createFileRoute("/grn_/build")({
  validateSearch: (search: Record<string, unknown>): { editId?: string } => ({
    editId: typeof search.editId === "string" ? search.editId : undefined,
  }),
  head: () => ({
    meta: [{ title: "Build GR — ProcureFlow" }],
  }),
  component: BuildGRPage,
});

const CONDITIONS: GRNCondition[] = ["Good", "Damaged", "Rejected"];

interface RowState {
  key: string;
  sourcePoId: string;
  sourcePoNumber: string;
  sourcePoLineItemId: string;
  lineDescription: string;
  lineUom: string;
  lineRemaining: number;
  quantityReceived: number;
  condition: GRNCondition;
  notes: string;
}

function newRow(): RowState {
  return {
    key: crypto.randomUUID(),
    sourcePoId: "",
    sourcePoNumber: "",
    sourcePoLineItemId: "",
    lineDescription: "",
    lineUom: "",
    lineRemaining: 0,
    quantityReceived: 1,
    condition: "Good",
    notes: "",
  };
}

/** Today's date as YYYY-MM-DD (UTC) for the default date received. */
function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function BuildGRPage() {
  const navigate = useNavigate();
  const { editId } = Route.useSearch();
  const isEdit = Boolean(editId);

  const [pos, setPos] = useState<PurchaseOrderListItemDto[]>([]);
  const [receiverName, setReceiverName] = useState("");
  const [dateReceived, setDateReceived] = useState(todayIso());
  const [deliveryReference, setDeliveryReference] = useState("");
  const [rows, setRows] = useState<RowState[]>([newRow()]);
  // Cache of PO detail (lines) by PO id, lazily fetched when a PO is picked.
  const [poDetails, setPoDetails] = useState<Record<string, PurchaseOrderDetailDto>>({});
  const [loading, setLoading] = useState(true);
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        // fetchWithAuth awaits mswReady internally so the MSW mount-time fetch
        // pattern is satisfied. Only receivable POs (Sent / PartiallyReceived)
        // can be received against.
        const res = await fetchWithAuth(
          "/api/purchase-orders?status=Sent,PartiallyReceived&limit=100",
        );
        const data = res.ok
          ? ((await res.json()) as { data: PurchaseOrderListItemDto[] }).data
          : [];
        if (cancelled) return;
        setPos(data ?? []);

        // US-077 edit mode — pre-load the existing GRN's header + lines. A Good line's
        // source PO line "remaining" is bumped by that line's own received qty so editing
        // it isn't blocked (server re-checks the effective remaining).
        if (editId) {
          const grnRes = await fetchWithAuth(`/api/grns/${editId}`);
          if (grnRes.ok) {
            const { data: grn } = (await grnRes.json()) as { data: GRNDetailDto };
            if (cancelled) return;
            setReceiverName(grn.receiver_name);
            setDateReceived(grn.date_received);
            setDeliveryReference(grn.delivery_reference ?? "");
            const sourcePoIds = [
              ...new Set(
                grn.line_items.map((li) => li.source_po_id).filter((x): x is string => !!x),
              ),
            ];
            const detailEntries = await Promise.all(
              sourcePoIds.map(async (pid) => {
                const r = await fetchWithAuth(`/api/purchase-orders/${pid}`);
                return r.ok
                  ? ([pid, ((await r.json()) as { data: PurchaseOrderDetailDto }).data] as const)
                  : null;
              }),
            );
            const detailMap: Record<string, PurchaseOrderDetailDto> = {};
            for (const e of detailEntries) if (e) detailMap[e[0]] = e[1];
            if (cancelled) return;
            setPoDetails(detailMap);
            const editRows: RowState[] = grn.line_items
              .filter((li) => li.source_po_id && li.source_po_line_item_id)
              .map((li) => {
                const poDetail = li.source_po_id ? detailMap[li.source_po_id] : undefined;
                const poLine = poDetail?.line_items.find((p) => p.id === li.source_po_line_item_id);
                const currentRemaining = poLine ? poLineRemaining(poLine) : 0;
                return {
                  key: crypto.randomUUID(),
                  sourcePoId: li.source_po_id ?? "",
                  sourcePoNumber: li.source_po_number ?? "",
                  sourcePoLineItemId: li.source_po_line_item_id ?? "",
                  lineDescription: li.po_line_description,
                  lineUom: "",
                  // Bump by this line's own Good receipt so its qty can be kept/raised.
                  lineRemaining:
                    currentRemaining + (li.condition === "Good" ? li.quantity_received : 0),
                  quantityReceived: li.quantity_received,
                  condition: li.condition,
                  notes: li.notes ?? "",
                };
              });
            if (editRows.length > 0) setRows(editRows);
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [editId]);

  // Lines of a PO with remaining receivable > 0, from the cached detail.
  function availableLines(poId: string): POLineItemDto[] {
    const detail = poDetails[poId];
    if (!detail) return [];
    return detail.line_items.filter((li) => poLineRemaining(li) > 0);
  }

  async function pickPo(rowKey: string, po: PurchaseOrderListItemDto) {
    if (!poDetails[po.id]) {
      const res = await fetchWithAuth(`/api/purchase-orders/${po.id}`);
      if (res.ok) {
        const { data } = (await res.json()) as { data: PurchaseOrderDetailDto };
        setPoDetails((prev) => ({ ...prev, [po.id]: data }));
      }
    }
    setRows((prev) =>
      prev.map((r) =>
        r.key === rowKey
          ? {
              ...r,
              sourcePoId: po.id,
              sourcePoNumber: po.po_number,
              // Reset the line selection when the PO changes.
              sourcePoLineItemId: "",
              lineDescription: "",
              lineUom: "",
              lineRemaining: 0,
            }
          : r,
      ),
    );
  }

  function pickLine(rowKey: string, line: POLineItemDto) {
    const remaining = poLineRemaining(line);
    setRows((prev) =>
      prev.map((r) =>
        r.key === rowKey
          ? {
              ...r,
              sourcePoLineItemId: line.id,
              lineDescription: line.description,
              lineUom: line.unit_of_measure,
              lineRemaining: remaining,
              quantityReceived: Math.min(r.quantityReceived || 1, remaining) || 1,
            }
          : r,
      ),
    );
  }

  function updateQty(rowKey: string, qty: number) {
    setRows((prev) => prev.map((r) => (r.key === rowKey ? { ...r, quantityReceived: qty } : r)));
  }

  function updateCondition(rowKey: string, condition: GRNCondition) {
    setRows((prev) => prev.map((r) => (r.key === rowKey ? { ...r, condition } : r)));
  }

  function updateNotes(rowKey: string, notes: string) {
    setRows((prev) => prev.map((r) => (r.key === rowKey ? { ...r, notes } : r)));
  }

  function addRow() {
    setRows((prev) => [...prev, newRow()]);
  }

  function removeRow(rowKey: string) {
    setRows((prev) => (prev.length > 1 ? prev.filter((r) => r.key !== rowKey) : prev));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFormError(null);

    const modelRows: GRNBuildRowModel[] = rows
      .filter((r) => r.sourcePoLineItemId)
      .map((r) => ({
        sourcePoId: r.sourcePoId,
        sourcePoLineItemId: r.sourcePoLineItemId,
        quantityReceived: r.quantityReceived,
        condition: r.condition,
        notes: r.notes,
      }));
    // The over-receipt guard sums per line; only Good-condition quantities
    // consume remaining receivable (damaged / rejected do not count toward
    // fulfilment), so the guard runs against Good rows. The server is
    // authoritative — this just keeps the user out of an obvious 400.
    const remainingById = new Map<string, number>();
    rows.forEach((r) => {
      if (r.sourcePoLineItemId) remainingById.set(r.sourcePoLineItemId, r.lineRemaining);
    });
    const goodRows = modelRows.filter((r) => r.condition === "Good");
    const validationError = validateGRNBuildRows(
      goodRows.length > 0 ? goodRows : modelRows,
      remainingById,
      receiverName,
    );
    if (validationError) {
      setFormError(validationError);
      return;
    }

    setSubmitting(true);
    try {
      const lines = modelRows.map((r) => ({
        source_po_line_item_id: r.sourcePoLineItemId,
        quantity_received: r.quantityReceived,
        condition: r.condition,
        notes: r.notes?.trim() || undefined,
      }));
      if (isEdit && editId) {
        const body: UpdateGRNRequest = {
          receiver_name: receiverName.trim(),
          date_received: dateReceived,
          delivery_reference: deliveryReference.trim() || undefined,
          lines,
        };
        const res = await fetchWithAuth(`/api/grns/${editId}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
        if (!res.ok) {
          const errJson = await res.json().catch(() => ({}) as { message?: string });
          setFormError(errJson?.message ?? "Failed to save changes.");
          return;
        }
        navigate({ to: "/grn/$id", params: { id: editId } });
        return;
      }
      const body: CreateGRNFromPOsRequest = {
        receiver_name: receiverName.trim(),
        date_received: dateReceived,
        delivery_reference: deliveryReference.trim() || undefined,
        lines,
      };
      const res = await fetchWithAuth("/api/grns/from-pos", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (res.status !== 201) {
        const errJson = await res.json().catch(() => ({}) as { message?: string });
        setFormError(errJson?.message ?? "Failed to record the goods receipt.");
        return;
      }
      const okJson = (await res.json()) as { data: GRNDetailDto };
      sessionStorage.setItem("grn:justRecorded", okJson.data.id);
      navigate({ to: "/grn/$id", params: { id: okJson.data.id } });
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="h-6 w-6 rounded-full border-2 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <Link
        to="/grn"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Goods Receipt Notes
      </Link>

      <div className="rounded-2xl bg-card border border-border shadow-elegant p-6 space-y-2">
        <h1 className="text-lg font-semibold tracking-tight">
          {isEdit ? "Edit draft goods receipt" : "Build GR from POs"}
        </h1>
        <p className="text-sm text-muted-foreground">
          {isEdit
            ? "Adjust the header and received lines. Quantity changes re-sync the source purchase order's receipt."
            : "Record a single goods receipt covering deliveries from one or more purchase orders. Each line traces back to the PO line it was received against."}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Receipt header. */}
        <section className="rounded-2xl bg-card border border-border shadow-elegant overflow-hidden">
          <div className="px-5 py-4 border-b border-border">
            <h2 className="text-sm font-semibold">Receipt details</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Who received the goods, when, and any delivery reference.
            </p>
          </div>
          <div className="px-5 py-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="receiver-name">Receiver name</Label>
              <Input
                id="receiver-name"
                value={receiverName}
                onChange={(e) => setReceiverName(e.target.value)}
                placeholder="e.g. Jordan Lee"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="date-received">Date received</Label>
              <Input
                id="date-received"
                type="date"
                value={dateReceived}
                onChange={(e) => setDateReceived(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="delivery-reference">Delivery reference</Label>
              <Input
                id="delivery-reference"
                value={deliveryReference}
                onChange={(e) => setDeliveryReference(e.target.value)}
                placeholder="Optional"
              />
            </div>
          </div>
        </section>

        {/* Line builder — two cascading lookups + qty / condition / notes per row. */}
        <section className="rounded-2xl bg-card border border-border shadow-elegant overflow-hidden">
          <div className="px-5 py-4 border-b border-border flex items-center justify-between">
            <div>
              <h2 className="text-sm font-semibold">Lines</h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                Pick a source PO, then a line of that PO. Received quantity cannot exceed the
                remaining receivable amount.
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addRow}
              data-testid="add-line-button"
            >
              <Plus className="h-3.5 w-3.5" /> Add line
            </Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-muted-foreground bg-muted/40">
                <tr>
                  <th className="px-4 py-2.5 text-left w-48">Source PO</th>
                  <th className="px-4 py-2.5 text-left">PO line</th>
                  <th className="px-3 py-2.5 text-left w-28">Remaining</th>
                  <th className="px-3 py-2.5 text-left w-24">Received</th>
                  <th className="px-3 py-2.5 text-left w-32">Condition</th>
                  <th className="px-3 py-2.5 text-left w-40">Notes</th>
                  <th className="px-3 py-2.5 w-12" />
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => (
                  <tr key={row.key} className="border-t border-border align-top">
                    {/* Lookup 1 — source PO */}
                    <td className="px-4 py-2">
                      <Popover>
                        <PopoverTrigger asChild>
                          <button
                            type="button"
                            aria-label={`Line ${idx + 1} source PO`}
                            className="inline-flex w-full items-center justify-between gap-2 rounded border border-border bg-background px-2.5 py-2 text-left text-xs hover:bg-accent transition"
                          >
                            <span
                              className={row.sourcePoNumber ? "font-mono" : "text-muted-foreground"}
                            >
                              {row.sourcePoNumber || "Select a PO…"}
                            </span>
                            <Search className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent align="start" className="w-80 p-0 overflow-hidden">
                          <div className="max-h-60 overflow-y-auto">
                            {pos.length === 0 ? (
                              <p className="px-3 py-3 text-xs text-muted-foreground">
                                No receivable POs available.
                              </p>
                            ) : (
                              <ul>
                                {pos.map((po) => (
                                  <li key={po.id}>
                                    <button
                                      type="button"
                                      data-testid={`po-option-${po.id}`}
                                      onClick={() => pickPo(row.key, po)}
                                      className="grid w-full grid-cols-[8rem_minmax(0,1fr)] items-center gap-2 border-t border-border/60 px-3 py-2 text-left text-xs transition hover:bg-accent first:border-t-0"
                                    >
                                      <span className="truncate font-mono font-medium">
                                        {po.po_number}
                                      </span>
                                      <span className="truncate text-muted-foreground">
                                        {po.vendor_name}
                                      </span>
                                    </button>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        </PopoverContent>
                      </Popover>
                    </td>

                    {/* Lookup 2 — PO line (only lines with remaining > 0) */}
                    <td className="px-4 py-2">
                      <Popover>
                        <PopoverTrigger asChild>
                          <button
                            type="button"
                            aria-label={`Line ${idx + 1} PO line`}
                            disabled={!row.sourcePoId}
                            className="inline-flex w-full items-center justify-between gap-2 rounded border border-border bg-background px-2.5 py-2 text-left text-xs hover:bg-accent transition disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <span className={row.lineDescription ? "" : "text-muted-foreground"}>
                              {row.lineDescription || "Select a line…"}
                            </span>
                            <Search className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent align="start" className="w-96 p-0 overflow-hidden">
                          <div className="max-h-60 overflow-y-auto">
                            {availableLines(row.sourcePoId).length === 0 ? (
                              <p className="px-3 py-3 text-xs text-muted-foreground">
                                No lines with remaining receivable quantity on this PO.
                              </p>
                            ) : (
                              <ul>
                                {availableLines(row.sourcePoId).map((line) => (
                                  <li key={line.id}>
                                    <button
                                      type="button"
                                      data-testid={`po-line-option-${line.id}`}
                                      onClick={() => pickLine(row.key, line)}
                                      className="grid w-full grid-cols-[minmax(0,1fr)_4rem] items-center gap-2 border-t border-border/60 px-3 py-2 text-left text-xs transition hover:bg-accent first:border-t-0"
                                    >
                                      <span className="truncate">{line.description}</span>
                                      <span className="text-right tabular-nums text-muted-foreground">
                                        {poLineRemaining(line)} {line.unit_of_measure}
                                      </span>
                                    </button>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        </PopoverContent>
                      </Popover>
                    </td>

                    <td className="px-3 py-2 text-xs tabular-nums text-muted-foreground">
                      {row.sourcePoLineItemId ? `${row.lineRemaining} ${row.lineUom}` : "—"}
                    </td>

                    <td className="px-3 py-2">
                      <Input
                        aria-label={`Line ${idx + 1} received quantity`}
                        type="number"
                        min={1}
                        max={row.lineRemaining || undefined}
                        value={row.quantityReceived}
                        disabled={!row.sourcePoLineItemId}
                        onChange={(e) => updateQty(row.key, parseInt(e.target.value, 10) || 0)}
                      />
                    </td>

                    <td className="px-3 py-2">
                      <Select
                        value={row.condition}
                        onValueChange={(v) => updateCondition(row.key, v as GRNCondition)}
                        disabled={!row.sourcePoLineItemId}
                      >
                        <SelectTrigger aria-label={`Line ${idx + 1} condition`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {CONDITIONS.map((c) => (
                            <SelectItem key={c} value={c} data-testid={`condition-option-${c}`}>
                              {c}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>

                    <td className="px-3 py-2">
                      <Input
                        aria-label={`Line ${idx + 1} notes`}
                        value={row.notes}
                        disabled={!row.sourcePoLineItemId}
                        placeholder="Optional"
                        onChange={(e) => updateNotes(row.key, e.target.value)}
                      />
                    </td>

                    <td className="px-3 py-2 text-right">
                      <button
                        type="button"
                        aria-label={`Remove line ${idx + 1}`}
                        onClick={() => removeRow(row.key)}
                        disabled={rows.length === 1}
                        className="rounded p-1.5 text-muted-foreground hover:bg-accent hover:text-destructive transition disabled:opacity-30"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {formError && (
          <div
            role="alert"
            className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive"
          >
            {formError}
          </div>
        )}

        <div className="flex items-center justify-end gap-2">
          <Button asChild variant="outline" type="button">
            <Link to="/grn">Cancel</Link>
          </Button>
          <Button type="submit" disabled={submitting} data-testid="submit-build-button">
            <Send className="h-3.5 w-3.5" />
            {submitting
              ? isEdit
                ? "Saving…"
                : "Recording…"
              : isEdit
                ? "Save changes"
                : "Record GR"}
          </Button>
        </div>
      </form>
    </div>
  );
}
